import { MercatorCoordinate } from 'maplibre-gl';
import { NIGHT_HAWK_RENDERED_TILT_LIMIT } from '../domain/night-hawk-keyframes';

export interface NightHawkCoordinates { rotation: number; tilt: number; altitude: number }
export type ElevationReader = (point: readonly [number, number], signal?: AbortSignal) => Promise<number>;
export const NIGHT_HAWK_MAX_TILT = NIGHT_HAWK_RENDERED_TILT_LIMIT;

/** Solve platform position against its local terrain while retaining a ground target. */
export async function solveNightHawkGeometry(target: readonly [number, number], values: NightHawkCoordinates,
  elevationAt: ElevationReader, signal?: AbortSignal, targetElevation?: number) {
  const aim = targetElevation ?? await elevationAt(target, signal);
  const origin = MercatorCoordinate.fromLngLat([...target], aim);
  const tilt = Math.max(-NIGHT_HAWK_MAX_TILT, Math.min(NIGHT_HAWK_MAX_TILT, values.tilt));
  const tangent = Math.tan(Math.abs(tilt) * Math.PI / 180), bearing = (values.rotation + (tilt < 0 ? 180 : 0)) * Math.PI / 180;
  const at = async (distance: number) => {
    signal?.throwIfAborted();
    const point = new MercatorCoordinate(origin.x - Math.sin(bearing) * distance,
      origin.y + Math.cos(bearing) * distance).toLngLat();
    if (Math.abs(point.lat) > 85.051128) throw new RangeError('Camera position exceeds the map latitude limit. Reduce tilt or altitude.');
    const ground = [point.lng, point.lat] as const;
    const terrain = await elevationAt(ground, signal);
    const position = MercatorCoordinate.fromLngLat([...ground], terrain + values.altitude);
    return { ground, terrain, position, error: position.z - origin.z - (tangent === 0 ? 0 : distance / tangent) };
  };
  let solved = await at(0);
  if (tilt !== 0) {
    let low = 0, high = values.altitude * origin.meterInMercatorCoordinateUnits() * tangent * 2;
    let upper = await at(high);
    for (let i = 0; upper.error > 0 && i < 12; i++) { high *= 2; upper = await at(high); }
    if (upper.error > 0) throw new RangeError('Cannot resolve this altitude and tilt against terrain.');
    for (let i = 0; i < 28; i++) {
      const distance = (low + high) / 2;
      solved = await at(distance);
      if (solved.error > 0) low = distance; else high = distance;
    }
  }
  signal?.throwIfAborted();
  return { ...solved, aim, target, values, tilt, altitudeMsl: solved.terrain + values.altitude,
    slant: Math.hypot(solved.position.x - origin.x, solved.position.y - origin.y, solved.position.z - origin.z) };
}

/** Field of view required to retain the chosen map magnification at a physical range. */
export function nightHawkFramingFieldOfView(height: number, zoom: number, slant: number): number {
  return 2 * Math.atan(height / (2 * 512 * 2 ** zoom * slant)) * 180 / Math.PI;
}
